import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Check, Clock, User, LogIn, LogOut, Coffee, CoffeeIcon, AlertTriangle, Timer, Undo2, MapPin, Utensils } from 'lucide-react';
import { OffScheduleReasonModal } from '@/components/OffScheduleReasonModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_CONFIG = {
  ENTRY: {
    label: 'ENTREE',
    icon: LogIn,
    color: 'bg-green-500',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-500',
    textColor: 'text-green-700'
  },
  EXIT: {
    label: 'SORTIE',
    icon: LogOut,
    color: 'bg-red-500',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-500',
    textColor: 'text-red-700'
  },
  BREAK_START: {
    label: 'DEBUT DE PAUSE',
    icon: Coffee,
    color: 'bg-amber-500',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-500',
    textColor: 'text-amber-700'
  },
  BREAK_END: {
    label: 'FIN DE PAUSE',
    icon: CoffeeIcon,
    color: 'bg-blue-500',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-500',
    textColor: 'text-blue-700'
  },
  LUNCH_START: {
    label: 'DEBUT PAUSE DEJEUNER',
    icon: Utensils,
    color: 'bg-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-600',
    textColor: 'text-purple-700'
  },
  LUNCH_END: {
    label: 'FIN PAUSE DEJEUNER',
    icon: Utensils,
    color: 'bg-fuchsia-600',
    bgColor: 'bg-fuchsia-50',
    borderColor: 'border-fuchsia-600',
    textColor: 'text-fuchsia-700'
  }
};

const EmployeeConfirm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialValidationData = location.state?.validationData;

  const [validationData, setValidationData] = useState(initialValidationData);
  const [selectedType, setSelectedType] = useState(null);
  const [outsideReason, setOutsideReason] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('choose');
  // Iter 485 : motif de pointage hors planning
  const [offSchedule, setOffSchedule] = useState(null);
  const [offSubmitting, setOffSubmitting] = useState(false);

  const submitOffReason = async (reason, reasonNote) => {
    setOffSubmitting(true);
    try {
      await axios.post(`${API}/attendance/off-schedule-reason`, {
        event_id: offSchedule.event_id, reason, note: reasonNote || null,
      });
      toast.success('Motif enregistré, merci !');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement du motif");
    } finally {
      setOffSubmitting(false);
      setOffSchedule(null);
      navigate('/employee/scan');
    }
  };

  useEffect(() => {
    if (!validationData) {
      navigate('/employee/scan');
      return;
    }
    // If daily limit reached, show limit message
    if (validationData.daily_limit_reached) {
      setStep('daily_limit');
      return;
    }
    // If cross-store detected, show warning first
    if (validationData.is_cross_store) {
      if (validationData.choices?.length === 1) {
        setSelectedType(validationData.choices[0]);
      }
      setStep('cross_store_warning');
      return;
    }
    // If only one choice (ENTRY), auto-select but still show confirmation
    if (validationData.choices?.length === 1) {
      setSelectedType(validationData.choices[0]);
      if (validationData.is_outside_schedule) {
        setStep('outside_warning');
      } else {
        setStep('confirm');
      }
    }
  }, [validationData, navigate]);

  const handleCrossStoreConfirm = () => {
    // After confirming cross-store, proceed to normal flow
    if (selectedType) {
      if (validationData.is_outside_schedule) {
        setStep('outside_warning');
      } else {
        setStep('confirm');
      }
    } else {
      setStep('choose');
    }
  };

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    if (validationData.is_outside_schedule) {
      setStep('outside_warning');
    } else {
      setStep('confirm');
    }
  };

  const handleOutsideReasonSelect = (reason) => {
    setOutsideReason(reason);
    setStep('confirm');
  };

  const handleConfirm = async () => {
    if (!selectedType) return;
    setLoading(true);

    // === GPS geofencing: ask for location, tolerate refusal ===
    let gps = { latitude: null, longitude: null, gps_denied: false };
    try {
      if (navigator.geolocation) {
        const pos = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 6000,
            maximumAge: 30000,
          });
        });
        gps = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          gps_denied: false,
        };
      } else {
        gps.gps_denied = true;
      }
    } catch (_err) {
      // User refused or timeout — we let the scan pass but flag it
      gps.gps_denied = true;
    }

    try {
      const token = localStorage.getItem('token');
      const confirmRes = await axios.post(
        `${API}/attendance/confirm`,
        {
          qr_token: validationData.qr_token,
          event_type: selectedType,
          overtime: validationData.suggested_sunday || false,
          sunday: validationData.suggested_sunday || false,
          holiday: validationData.suggested_holiday || false,
          note: note || null,
          outside_schedule_reason: outsideReason || null,
          scanned_store_id: validationData.scanned_store_id || null,
          latitude: gps.latitude,
          longitude: gps.longitude,
          gps_denied: gps.gps_denied,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const config = EVENT_CONFIG[selectedType];
      toast.success(`${config?.label || selectedType} enregistree avec succes !`);
      if (confirmRes.data?.lunch_warning) {
        toast.warning(confirmRes.data.lunch_warning, { duration: 10000 });
      }
      // Iter 485 : pointage hors planning -> popup motif avant de quitter
      if (confirmRes.data?.off_schedule) {
        setOffSchedule({ ...confirmRes.data.off_schedule, event_id: confirmRes.data.event_id });
        return;
      }
      navigate('/employee/scan');
    } catch (error) {
      console.error('Confirmation error:', error);
      const detail = error.response?.data?.detail || '';
      // Detect a transition/state-mismatch error and auto-recover by switching to the
      // correct choices instead of leaving the user stuck on the confirm screen.
      // Backend formats: "Transition invalide: ... Choix autorises: BREAK_START, EXIT"
      //                  "Pointage deja enregistre. Transition invalide: ... apres XXX. Actualisez et reessayez."
      const m = typeof detail === 'string' && detail.match(/Choix autorises\s*:\s*([A-Z_, ]+)/i);
      if (m) {
        const newChoices = m[1].split(',').map(s => s.trim()).filter(Boolean);
        if (newChoices.length > 0) {
          // Update the local state so the choose step shows the corrected options
          setValidationData(prev => ({ ...prev, choices: newChoices }));
          setSelectedType(newChoices.length === 1 ? newChoices[0] : null);
          setOutsideReason('');
          setNote('');
          setStep(newChoices.length === 1 ? 'confirm' : 'choose');
          // Friendly French message
          const labelOf = (t) => EVENT_CONFIG[t]?.label || t;
          const opts = newChoices.map(labelOf).join(' ou ');
          toast.error(`Vous avez deja pointe une entree. Choisissez : ${opts}.`);
          setLoading(false);
          return;
        }
      }
      toast.error(detail || "Erreur lors de l'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  if (!validationData) return null;

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Paris'
    });
  };

  const choices = validationData.daily_limit_reached ? [] : (validationData.choices || ['ENTRY']);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-lg"
      >
        <div className="bg-white rounded-2xl shadow-pop border-2 border-gray-100 overflow-hidden">

          {/* Header with user info + time */}
          <div className="p-5 bg-brand-blue/5 border-b-2 border-gray-200">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-12 h-12 rounded-full bg-brand-yellow flex items-center justify-center">
                <User className="w-6 h-6 text-brand-blue" />
              </div>
              <div>
                <p className="font-heading text-lg text-brand-blue">
                  {validationData.user.first_name} {validationData.user.last_name}
                </p>
                {validationData.schedule_info && (
                  <p className="text-xs text-gray-500 font-body">
                    Planning : {validationData.schedule_info}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center justify-center space-x-2 p-3 bg-white rounded-xl">
              <Clock className="w-5 h-5 text-brand-blue" />
              <span className="font-heading text-2xl text-brand-blue">
                {formatTime(validationData.timestamp)}
              </span>
            </div>
          </div>

          <div className="p-5">
            <AnimatePresence mode="wait">

              {/* Daily limit reached */}
              {step === 'daily_limit' && (
                <motion.div
                  key="daily_limit"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center space-y-4 py-4"
                >
                  <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto">
                    <AlertTriangle className="w-8 h-8 text-red-500" />
                  </div>
                  <h2 className="font-heading text-xl text-red-600">
                    Limite journaliere atteinte
                  </h2>
                  <p className="text-gray-600 font-body text-sm">
                    Vous avez deja effectue le nombre maximum de pointages pour aujourd'hui
                    (2 entrees et 2 sorties).
                  </p>
                  {validationData.scan_counts && (
                    <div className="bg-gray-50 rounded-xl p-3 text-sm text-gray-500 space-y-1">
                      <p>Entrees : {validationData.scan_counts.ENTRY}/2</p>
                      <p>Sorties : {validationData.scan_counts.EXIT}/2</p>
                    </div>
                  )}
                  <p className="text-xs text-gray-400 font-body">
                    Contactez votre responsable en cas de besoin.
                  </p>
                  <Button
                    className="w-full bg-brand-blue hover:bg-brand-blue/90 text-white mt-2"
                    onClick={() => navigate('/employee/dashboard')}
                    data-testid="back-to-dashboard-btn"
                  >
                    Retour au tableau de bord
                  </Button>
                </motion.div>
              )}

              {/* Step 1: Choose event type */}
              {step === 'choose' && (
                <motion.div
                  key="choose"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h2 className="font-heading text-xl text-center text-brand-blue mb-2">
                    Que souhaitez-vous enregistrer ?
                  </h2>

                  {validationData.lunch_break_minutes > 0 && !validationData.lunch_taken && (
                    <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-xl px-3 py-2 text-xs text-orange-700" data-testid="lunch-info-banner">
                      <Utensils className="w-4 h-4 flex-shrink-0" />
                      <span>
                        Pause dejeuner attribuee : <strong>{Math.floor(validationData.lunch_break_minutes / 60) > 0 ? `${Math.floor(validationData.lunch_break_minutes / 60)}h${String(validationData.lunch_break_minutes % 60).padStart(2, '0')}` : `${validationData.lunch_break_minutes} min`}</strong> — a prendre entre 11h00 et 15h00 (une seule fois par jour).
                      </span>
                    </div>
                  )}

                  <div className="space-y-3">
                    {choices.map((type) => {
                      const config = EVENT_CONFIG[type];
                      if (!config) return null;
                      const Icon = config.icon;
                      return (
                        <motion.button
                          key={type}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleTypeSelect(type)}
                          className={`w-full p-4 rounded-xl border-2 ${config.borderColor} ${config.bgColor} flex items-center space-x-4 transition-all hover:shadow-md`}
                          data-testid={`choice-${type.toLowerCase()}`}
                        >
                          <div className={`w-12 h-12 rounded-full ${config.color} flex items-center justify-center`}>
                            <Icon className="w-6 h-6 text-white" />
                          </div>
                          <span className={`font-heading text-lg ${config.textColor}`}>
                            {config.label}
                          </span>
                        </motion.button>
                      );
                    })}
                  </div>

                  <Button
                    variant="ghost"
                    className="w-full mt-2 text-gray-500"
                    onClick={() => navigate('/employee/scan')}
                    data-testid="cancel-button"
                  >
                    Annuler
                  </Button>
                </motion.div>
              )}

              {/* Cross-store warning */}
              {step === 'cross_store_warning' && (
                <motion.div
                  key="cross_store"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <div className="flex items-center justify-center mb-2">
                    <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center">
                      <MapPin className="w-8 h-8 text-orange-600" />
                    </div>
                  </div>

                  <h2 className="font-heading text-lg text-center text-orange-700" data-testid="cross-store-title">
                    Point de vente different
                  </h2>

                  <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-4 space-y-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-body">Votre magasin</p>
                        <p className="font-heading text-sm text-blue-700" data-testid="employee-store-name">
                          {validationData.employee_store_name || 'Non defini'}
                        </p>
                      </div>
                    </div>
                    <div className="border-t border-orange-200"></div>
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-orange-200 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-orange-700" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-body">Vous pointez depuis</p>
                        <p className="font-heading text-sm text-orange-700" data-testid="scanned-store-name">
                          {validationData.scanned_store_name || 'Inconnu'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <p className="text-sm text-center text-gray-600 font-body">
                    Vous etes sur le point de pointer depuis un autre magasin.
                    Votre responsable sera notifie. Voulez-vous continuer ?
                  </p>

                  <div className="flex space-x-3">
                    <Button
                      variant="outline"
                      className="flex-1 h-12 border-2 border-gray-300"
                      onClick={() => navigate('/employee/scan')}
                      data-testid="cross-store-cancel"
                    >
                      Annuler
                    </Button>
                    <Button
                      className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white font-heading shadow-pop-yellow active:translate-y-1 active:shadow-none transition-all"
                      onClick={handleCrossStoreConfirm}
                      data-testid="cross-store-continue"
                    >
                      Continuer
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Step: Outside schedule warning */}
              {step === 'outside_warning' && (
                <motion.div
                  key="outside"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <div className="flex items-center justify-center mb-2">
                    <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
                      <AlertTriangle className="w-8 h-8 text-amber-600" />
                    </div>
                  </div>

                  <h2 className="font-heading text-lg text-center text-amber-700">
                    Pointage hors planning
                  </h2>
                  <p className="text-sm text-center text-gray-600 font-body">
                    Vous pointez en dehors de vos horaires habituels.
                    {validationData.schedule_info && (
                      <span className="block mt-1 font-semibold text-brand-blue">
                        Planning prevu : {validationData.schedule_info}
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-center text-gray-500 font-body">
                    Veuillez indiquer la raison :
                  </p>

                  <div className="space-y-3">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleOutsideReasonSelect('overtime')}
                      className="w-full p-4 rounded-xl border-2 border-green-300 bg-green-50 flex items-center space-x-4 hover:shadow-md transition-all"
                      data-testid="reason-overtime"
                    >
                      <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                        <Timer className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-left">
                        <span className="font-heading text-base text-green-700 block">Heures supplementaires</span>
                        <span className="text-xs text-green-600 font-body">Travail au-dela des horaires prevus</span>
                      </div>
                    </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleOutsideReasonSelect('makeup_hours')}
                      className="w-full p-4 rounded-xl border-2 border-blue-300 bg-blue-50 flex items-center space-x-4 hover:shadow-md transition-all"
                      data-testid="reason-makeup"
                    >
                      <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center">
                        <Undo2 className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-left">
                        <span className="font-heading text-base text-blue-700 block">Rattrapage d'heures</span>
                        <span className="text-xs text-blue-600 font-body">Recuperation d'heures non effectuees</span>
                      </div>
                    </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleOutsideReasonSelect('forgotten_clockin')}
                      className="w-full p-4 rounded-xl border-2 border-orange-300 bg-orange-50 flex items-center space-x-4 hover:shadow-md transition-all"
                      data-testid="reason-forgotten"
                    >
                      <div className="w-12 h-12 rounded-full bg-orange-500 flex items-center justify-center">
                        <AlertTriangle className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-left">
                        <span className="font-heading text-base text-orange-700 block">Oubli de pointage</span>
                        <span className="text-xs text-orange-600 font-body">J'ai oublie de pointer a l'heure prevue</span>
                      </div>
                    </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleOutsideReasonSelect('other')}
                      className="w-full p-4 rounded-xl border-2 border-gray-300 bg-gray-50 flex items-center space-x-4 hover:shadow-md transition-all"
                      data-testid="reason-other"
                    >
                      <div className="w-12 h-12 rounded-full bg-gray-500 flex items-center justify-center">
                        <MapPin className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-left">
                        <span className="font-heading text-base text-gray-700 block">Autre</span>
                        <span className="text-xs text-gray-600 font-body">Autre motif a preciser</span>
                      </div>
                    </motion.button>
                  </div>

                  <Button
                    variant="ghost"
                    className="w-full mt-2 text-gray-500"
                    onClick={() => { setStep('choose'); setSelectedType(null); }}
                    data-testid="back-button"
                  >
                    Retour
                  </Button>
                </motion.div>
              )}

              {/* Step 3: Final confirmation */}
              {step === 'confirm' && selectedType && (
                <motion.div
                  key="confirm"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  {(() => {
                    const config = EVENT_CONFIG[selectedType];
                    const Icon = config.icon;
                    return (
                      <div className={`p-4 rounded-xl ${config.bgColor} border-2 ${config.borderColor} flex items-center space-x-4`}>
                        <div className={`w-14 h-14 rounded-full ${config.color} flex items-center justify-center`}>
                          <Icon className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <p className={`font-heading text-xl ${config.textColor}`}>{config.label}</p>
                          {outsideReason && (
                            <p className="text-xs text-amber-600 font-semibold mt-0.5">
                              {outsideReason === 'overtime' ? 'Heures supplementaires' : outsideReason === 'makeup_hours' ? 'Rattrapage d\'heures' : outsideReason === 'forgotten_clockin' ? 'Oubli de pointage' : 'Autre'}
                            </p>
                          )}
                        </div>
                        <div className="ml-auto">
                          <Check className={`w-8 h-8 ${config.textColor}`} />
                        </div>
                      </div>
                    );
                  })()}

                  {/* Optional note */}
                  <div className="space-y-2">
                    <Label className="font-body font-semibold text-brand-blue text-sm">
                      Note (optionnel)
                    </Label>
                    <Textarea
                      placeholder="Ajouter un commentaire..."
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      className="min-h-16 border-2 focus:border-brand-blue text-sm"
                      data-testid="note-textarea"
                    />
                  </div>

                  {/* Action buttons */}
                  <div className="flex space-x-3">
                    <Button
                      variant="outline"
                      className="flex-1 h-12 border-2"
                      onClick={() => {
                        if (validationData.is_outside_schedule && choices.length === 1) {
                          setStep('outside_warning');
                        } else {
                          setStep('choose');
                          setSelectedType(null);
                          setOutsideReason('');
                        }
                      }}
                      data-testid="back-confirm-button"
                    >
                      Retour
                    </Button>
                    <Button
                      className="flex-1 h-12 bg-brand-blue hover:bg-brand-blue/90 text-white font-heading uppercase shadow-pop-yellow active:translate-y-1 active:shadow-none transition-all"
                      onClick={handleConfirm}
                      disabled={loading}
                      data-testid="confirm-button"
                    >
                      {loading ? 'Enregistrement...' : 'Valider'}
                    </Button>
                  </div>
                </motion.div>
              )}

            </AnimatePresence>
          </div>
        </div>
      </motion.div>
      <OffScheduleReasonModal open={!!offSchedule} offSchedule={offSchedule} onSubmit={submitOffReason} submitting={offSubmitting} />
    </div>
  );
};

export default EmployeeConfirm;
